import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { rateSummary } from '../actions';
import { getPetNameFromValue } from '../util';

const useRateSummary = () => {
  const store = useSelector(({ quoting }) => quoting, shallowEqual);
  const dispatch = useDispatch();
  const {
    customerZipCode,
    data,
    effectiveDateCustom,
    guidData,
    parameters,
    plans,
    quoteSelectedValues,
    sessionInformation: { nopCommerceUser, userEmail },
    openQuoteGuid,
  } = store;

  const getSelectedRateBody = useMemo(() => {
    if (!data) {
      return {};
    }

    const petQuotes = [];

    data.ebPetQuoteResponseList.forEach((quote) => {
      const selectedValues = quoteSelectedValues.find(
        (item) => item.quoteId === quote.petQuoteId,
      );

      if (!selectedValues) {
        return;
      }

      const selectedPackagePlan = quote.PrePackagedPlans
        .find((item) => item.Id === selectedValues.selectedPlanPackaged);

      let reimbursement = selectedValues.reimbursementId;
      let deductible = selectedValues.deductibleId;

      if (selectedPackagePlan) {
        reimbursement = selectedPackagePlan.ReimbursementId;
        deductible = selectedPackagePlan.DeductibleId;
      }

      const { selectedPlan } = selectedValues;

      const selectedPlanMatch = plans.find(({ id }) => id === selectedPlan);
      let planId = selectedPlanMatch?.id;
      let { selectedPlanIndex } = selectedValues;

      if (!planId) {
        //  plan do not exist on new product (possible zipcode changed)
        const defaultPrePackagePlan = quote.PrePackagedPlans[0];

        reimbursement = defaultPrePackagePlan.ReimbursementId;
        deductible = defaultPrePackagePlan.DeductibleId;
        planId = defaultPrePackagePlan.PlanId;
        selectedPlanIndex = plans.findIndex(({ id }) => id === selectedPlan);
      }

      petQuotes.push({
        deductible,
        Id: quote.petQuoteId,
        Modifiers: quote.modifiers || [],
        PetAge: quote.petAgeName,
        PetBreed: quote.breedName,
        PetBreedId: quote.breedId,
        PetName: quote.petName,
        PetSex: quote.genderName,
        PetType: getPetNameFromValue(quote.petType),
        plan: planId,
        PlanType: selectedValues.prop103PlanType,
        reimbursement,
        selectedPlanIndex,
      });
    });

    const body = {
      DiamondClientId: nopCommerceUser.DiamonClientdId,
      ebGuID: guidData.GuID,
      EffectiveDate: null,
      eMail: userEmail || parameters.email,
      employerName: guidData.Name,
      GroupCode: data.groupCode,
      GroupCodeDscr: data.groupCodeDscr,
      IsEb: true,
      IsOpenQuote: !!openQuoteGuid,
      nopCommerceClientId: nopCommerceUser.CustomerNopCommerceId,
      petQuotes,
      QuoteGuid: openQuoteGuid || '',
      quoteId: 1,
      TestEffectiveDate: effectiveDateCustom,
      ZipCode: customerZipCode,
    };

    return body;
  }, [
    data,
    guidData,
    effectiveDateCustom,
    nopCommerceUser.DiamonClientdId,
    nopCommerceUser.CustomerNopCommerceId,
    userEmail,
    parameters.email,
    customerZipCode,
    quoteSelectedValues,
    plans,
    openQuoteGuid,
  ]);

  const getSelectedRate = useCallback(async (testdate) => {
    const body = testdate ? {
      ...getSelectedRateBody,
      TestEffectiveDate: testdate,
    } : getSelectedRateBody;
    await dispatch(rateSummary(body));
  }, [dispatch, getSelectedRateBody]);

  return { getSelectedRate, getSelectedRateBody };
};

export { useRateSummary };
